import { DEFAULT_BASE_URL } from '../constants/defaults';
import {
  AutorenderAuthError,
  AutorenderNetworkError,
  AutorenderUploadError,
} from '../errors';
import type { AppRunnerResponse, UploadSettings } from '../types';

interface UploadFileParams {
  file: File;
  folderPath?: string;
  relativePath?: string;
  settings?: UploadSettings;
}

interface NewUploadResponse {
  id: string;
  workspace_id: string;
  name: string;
  path: string;
  url: string;
  width?: number | null;
  height?: number | null;
  size: number;
  format?: string;
  mime_type?: string;
  hash?: string;
  tags?: string[];
  metadata?: Record<string, unknown> | null;
  custom_id?: string | null;
  is_private?: boolean;
  is_duplicate?: boolean;
  created_at: string;
  upload_source?: string;
  folder_no?: string | null;
  file_no?: string;
}

export class AutorenderApiClient {
  private apiKey: string;
  private baseUrl: string;

  constructor(opts: { apiKey: string; baseUrl?: string }) {
    if (!opts.apiKey) {
      throw new Error('apiKey is required');
    }
    this.apiKey = opts.apiKey;
    this.baseUrl = (opts.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, '');
  }

  updateOptions(opts: { apiKey?: string; baseUrl?: string }) {
    if (opts.apiKey !== undefined) {
      if (!opts.apiKey) {
        throw new Error('apiKey is required');
      }
      this.apiKey = opts.apiKey;
    }
    if ('baseUrl' in opts) {
      this.baseUrl = (opts.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, '');
    }
  }

  uploadFile(
    params: UploadFileParams,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<AppRunnerResponse> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }

      const xhr = new XMLHttpRequest();

      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort();
          reject(new DOMException('Upload aborted', 'AbortError'));
        });
      }

      let lastProgress = 0;
      xhr.upload.onprogress = event => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          lastProgress = progress;
          onProgress(progress);
        } else if (onProgress && event.loaded > 0) {
          const estimatedProgress = Math.min(
            95,
            Math.round((event.loaded / (event.loaded + 1000000)) * 100)
          );
          if (estimatedProgress > lastProgress) {
            lastProgress = estimatedProgress;
            onProgress(estimatedProgress);
          }
        }
      };

      xhr.onerror = () => {
        reject(new AutorenderNetworkError(`Upload failed: ${xhr.statusText || 'Network error'}`));
      };

      xhr.onload = () => {
        try {
          if (xhr.status < 200 || xhr.status >= 300) {
            const errorText = xhr.responseText || xhr.statusText;
            const message = `Upload failed (${xhr.status}): ${errorText || xhr.statusText}`;
            if (xhr.status === 401 || xhr.status === 403) {
              reject(new AutorenderAuthError(message));
              return;
            }
            reject(new AutorenderUploadError(message, xhr.status));
            return;
          }
          const newResponse = JSON.parse(xhr.responseText) as NewUploadResponse;

          const response: AppRunnerResponse = {
            success: true,
            file_no: newResponse.file_no || newResponse.id,
            name: newResponse.name,
            url: newResponse.url,
            file_size: newResponse.size,
            format: newResponse.format,
            width: newResponse.width,
            height: newResponse.height,
            created_at: newResponse.created_at,
            path: newResponse.path,
            workspace_no: newResponse.workspace_id,
            folder_no: newResponse.folder_no ?? undefined,
            isDuplicate: newResponse.is_duplicate || false,
          };

          resolve(response);
        } catch {
          reject(new AutorenderUploadError('Failed to parse response', xhr.status || 0));
        }
      };

      const endpoint = `${this.baseUrl}/uploads`;
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('file_name', params.file.name);
      if (params.folderPath) {
        formData.append('folder', params.folderPath);
      }
      if (params.settings?.tags?.length) {
        formData.append('tags', params.settings.tags.join(','));
      }
      if (params.settings?.pretransformations) {
        formData.append('transform', params.settings.pretransformations);
      }
      if (params.settings?.custom_id) {
        formData.append('custom_id', params.settings.custom_id);
      }
      if (params.settings?.random_suffix) {
        formData.append('random_prefix', 'true');
      }

      xhr.open('POST', endpoint);
      xhr.setRequestHeader('Authorization', `Bearer ${this.apiKey}`);
      xhr.setRequestHeader('accept', 'application/json');
      xhr.send(formData);
    });
  }
}

export {}
